/**
 * AbletonRow - an Ableton Control as a full row (DEC-291): tempo presets,
 * time signatures, Play / Stop / Continue, launch quantize values, or the
 * practice clock's Play / Stop. Record quantize's row is `RecQuant.tsx`.
 *
 * Each cell is one Ableton Control step (the same step a one-step button
 * carries), so a tap goes through the same dispatch a button press does.
 *
 * WHAT LIGHTS - the Rec Quantize row's rules, one authority per control
 * (`ableton/stepState.ts`):
 *
 *   lit cell     = what Live (or the practice clock) REPORTS, never what we
 *                  last sent. Live can be changed from its own window, from
 *                  OPSetlist and from OPBridge's dashboard.
 *   pending cell = sent, and the report has not agreed yet. Clears the moment
 *                  it does; after REC_QUANT_CONFIRM_MS it stops claiming to be
 *                  in flight. OPBridge answers a cheerful 200 for a send with
 *                  no M4L device loaded, so the round trip is the only proof.
 *   nothing lit  = Live is on a value this row does not offer, or we have not
 *                  heard. Both are honest.
 *
 * Continue never lights (it is a verb; once it lands Live is playing, which
 * Play shows), but its pending state clears when Live starts playing.
 *
 * Shares the Transpose row's visuals (`.transpose` / `.transpose-cell`,
 * skin-components.css 11k) and the Rec Quantize row's two-line face.
 */

import { For, createSignal, onCleanup } from 'solid-js';
import type { AbletonStep } from '@opbuttons/schema';
import { editOwnsGesture, type AbletonRowItem, type LayoutColor } from '../layout/store.ts';
import { REC_QUANT_CONFIRM_MS } from '../layout/projectStore.ts';
import { fireAction, fanoutTargets } from '../opbridge/dispatch.ts';
import { pokeClockPoll } from '../opbridge/clockPoll.ts';
import { createDiscreteGuard } from '../performance/guard.ts';
import { stepLit, stepLanded } from '../ableton/stepState.ts';
import { cellFace, cellKey, cellTitle } from '../ableton/rows.ts';
import { ulid } from '../utils/ulid.ts';

interface AbletonRowProps {
  item: AbletonRowItem;
}

const COLOR_TOKEN: Record<LayoutColor, string> = {
  cyan: 'idle',
  magenta: 'incoming',
  amber: 'armed',
  green: 'ok',
};

/** Steps that never leave the browser: firing one per bridge would apply it
 *  N times to one local clock. */
function isPracticeStep(s: AbletonStep): boolean {
  return s.type === 'practice.transport' || ('clock' in s && s.clock === 'practice');
}

export function AbletonRow(props: AbletonRowProps) {
  // Show-mode guard (SD-5): first tap arms the cell (2s), second sends.
  const guard = createDiscreteGuard(() => !!props.item.guard);

  // The cell we last sent and have not seen land. Cleared by the report
  // agreeing or by the timeout - never left set, because a cell stuck
  // claiming "in flight" forever is worse than one that says nothing.
  const [pending, setPending] = createSignal<string | null>(null);
  let pendingTimer: ReturnType<typeof setTimeout> | null = null;
  onCleanup(() => { if (pendingTimer !== null) clearTimeout(pendingTimer); });

  /** The pending cell's key while its send has not landed, else null. */
  const pendingKey = (): string | null => {
    const p = pending();
    if (p === null) return null;
    const cell = props.item.cells.find((c) => cellKey(c) === p);
    return cell && stepLanded(cell) !== true ? p : null;
  };

  function onCellDown(cell: AbletonStep, e: PointerEvent) {
    if (editOwnsGesture(props.item.id)) return;
    e.stopPropagation();
    const key = cellKey(cell);
    if (!guard.gate(key)) return; // guarded: first tap only armed

    setPending(key);
    if (pendingTimer !== null) clearTimeout(pendingTimer);
    pendingTimer = setTimeout(() => setPending(null), REC_QUANT_CONFIRM_MS);

    const targets = isPracticeStep(cell) ? [''] : fanoutTargets(props.item.bridgeIds);
    for (const target of targets) {
      void fireAction({ ...cell, id: ulid(), target });
    }
    // Do not wait out the poll interval to learn whether it landed.
    pokeClockPoll();
  }

  return (
    <div
      class="transpose abrow"
      data-token={COLOR_TOKEN[props.item.color]}
      data-control={props.item.control}
    >
      <div class="abrow-cells">
        <For each={props.item.cells}>
          {(cell) => {
            const f = () => cellFace(cell);
            const key = cellKey(cell);
            return (
              <button
                type="button"
                class="transpose-cell abrow-cell"
                data-cell={key}
                data-active={stepLit(cell) === true ? 'true' : 'false'}
                data-pending={pendingKey() === key ? 'true' : 'false'}
                data-armed={guard.armedKey() === key ? 'true' : 'false'}
                onPointerDown={(e) => onCellDown(cell, e)}
                title={cellTitle(cell)}
              >
                <span class="rq-l1">{f().l1}</span>
                {f().l2 !== null && <span class="rq-l2">{f().l2}</span>}
              </button>
            );
          }}
        </For>
      </div>
    </div>
  );
}
